// The pieces a folded card's header summary is made of (S136): small chips, a usage bar,
// one segment per item, a sparkline and a pulsing dot. A summary is one row that never wraps;
// the lead chip truncates last, and pieces marked `optional`/`extra` drop out under 10rem/15rem
// of room (container queries on the summary slot in ui/CollapsibleCard).
import type { ReactNode } from "react";

import { cn } from "./cn";
import type { Tone } from "./tones";

const CHIP: Record<Tone, string> = {
  green: "border-green/30 bg-green-lighter text-green-dark",
  orange: "border-orange/30 bg-orange-lighter text-orange-dark",
  red: "border-red/30 bg-red-lighter text-red-dark",
  blue: "border-blue/30 bg-blue-lighter text-blue-dark",
  gray: "border-gray-300 bg-gray-100 text-gray-700",
  primary: "border-primary/30 bg-primary-lighter text-primary-dark",
  secondary: "border-secondary/30 bg-secondary-lighter text-secondary-dark",
};

const FILL: Record<Tone, string> = {
  green: "bg-green",
  orange: "bg-orange",
  red: "bg-red",
  blue: "bg-blue",
  gray: "bg-gray-400",
  primary: "bg-primary",
  secondary: "bg-secondary",
};

/** Drops out when the header has less than 10rem (`optional`) or 15rem (`extra`) for the summary. */
export type Priority = "lead" | "optional" | "extra";
const SHOW: Record<Priority, string> = {
  lead: "flex",
  optional: "hidden @[10rem]:flex",
  extra: "hidden @[15rem]:flex",
};

/** Starts under the title on a narrow card, ends beside the chevron on a wide one. */
export function SummaryRow({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-center justify-start gap-1.5 overflow-hidden whitespace-nowrap @[26rem]/card:justify-end">{children}</div>
  );
}

export function Chip({
  tone = "gray",
  icon,
  title,
  priority = "lead",
  children,
}: {
  tone?: Tone;
  icon?: ReactNode;
  /** The full sentence behind a terse chip, for hover and screen readers. */
  title?: string;
  priority?: Priority;
  children: ReactNode;
}) {
  return (
    <span
      title={title}
      className={cn(
        "min-w-0 shrink items-center gap-1 rounded-full border px-2 py-0.5 text-[11.5px] font-semibold tabular-nums [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:shrink-0",
        CHIP[tone],
        SHOW[priority],
        priority === "lead" ? "" : "shrink-0",
      )}
    >
      {icon}
      <span className="truncate">{children}</span>
      {title ? <span className="sr-only">{title}</span> : null}
    </span>
  );
}

/** A usage bar (0–100) that turns orange from 85 %. */
export function MicroBar({ pct, label, priority = "optional" }: { pct: number; label: string; priority?: Priority }) {
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    <span role="img" aria-label={label} title={label} className={cn("h-1.5 w-10 shrink-0 overflow-hidden rounded-full bg-gray-200", SHOW[priority])}>
      <span className={cn("h-full rounded-full", clamped >= 85 ? FILL.orange : FILL.primary)} style={{ width: `${Math.max(clamped, 4)}%` }} />
    </span>
  );
}

/** One segment per item (at most 10), each in its own tone: requirements met and missing. */
export function Segments({ tones, label, priority = "optional" }: { tones: Tone[]; label: string; priority?: Priority }) {
  return (
    <span role="img" aria-label={label} title={label} className={cn("shrink-0 items-center gap-0.5", SHOW[priority])}>
      {tones.slice(0, 10).map((tone, i) => (
        <span key={i} className={cn("h-2.5 w-1.5 rounded-sm", FILL[tone])} />
      ))}
    </span>
  );
}

/** One series over time as a line with a light area under it; flat at the bottom when all zero. */
export function Sparkline({ values, label, priority = "optional" }: { values: number[]; label: string; priority?: Priority }) {
  const w = 56;
  const h = 18;
  if (values.length < 2) return null;
  const max = Math.max(...values, 0);
  const x = (i: number) => (i / (values.length - 1)) * (w - 2) + 1;
  const y = (v: number) => (max > 0 ? h - 2 - (v / max) * (h - 4) : h - 2);
  const line = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  return (
    <svg role="img" aria-label={label} viewBox={`0 0 ${w} ${h}`} width={w} height={h} className={cn("shrink-0 text-primary", SHOW[priority])}>
      <title>{label}</title>
      <polygon points={`1,${h - 1} ${line} ${w - 1},${h - 1}`} className="fill-current opacity-15" />
      <polyline points={line} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/** Something under way (a resize): a pulsing dot with its label for screen readers. */
export function PulseDot({ tone, label }: { tone: Tone; label: string }) {
  return (
    <span role="status" title={label} className="relative flex h-2 w-2 shrink-0">
      <span className={cn("absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 motion-reduce:animate-none", FILL[tone])} />
      <span className={cn("relative inline-flex h-2 w-2 rounded-full", FILL[tone])} />
      <span className="sr-only">{label}</span>
    </span>
  );
}
